import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import logoWhite from '../../images/logo-white.png';

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.ridenrepair.app';
const IOS_URL = 'https://apps.apple.com/in/app/ride-n-repair/id6446174085';
const SHIELD = '/rnr-shield.png';

// ---------------------------------------------------------------------------
// Funnel attribution (IMPL_05 §3.1): /app?r=<source>&bid=<booking_id>
// carries through to the Play listing via the Install Referrer param so the
// app can attribute the install on first run. iOS has no referrer equivalent
// (deferred phone-match server-side is the fallback).
// Accepted param names: r | ref (source), bid (booking id).
// ---------------------------------------------------------------------------
const SAFE_PARAM = /^[a-zA-Z0-9_-]{1,64}$/;

function readFunnelParams(search) {
  const out = { ref: null, bid: null };
  try {
    const params = new URLSearchParams(search || '');
    const rawRef = params.get('r') || params.get('ref');
    const rawBid = params.get('bid');
    if (rawRef && SAFE_PARAM.test(rawRef)) out.ref = rawRef;
    if (rawBid && SAFE_PARAM.test(rawBid)) out.bid = rawBid;
  } catch (e) { /* URLSearchParams unavailable — bare links still work */ }
  return out;
}

function playUrlWithReferrer({ ref, bid }) {
  if (!ref && !bid) return PLAY_URL;
  const referrerValue =
    `utm_source=${ref || 'web'}&utm_medium=app_page` + (bid ? `&bid=${bid}` : '');
  return `${PLAY_URL}&referrer=${encodeURIComponent(referrerValue)}`;
}

const C = { bg: '#0b0d10', card: '#15181d', border: 'rgba(255,255,255,.10)', red: '#f42222', redDeep: '#8b0f0f',
  white: '#fff', muted: 'rgba(255,255,255,.66)', faint: 'rgba(255,255,255,.42)' };
const FONT = "'Poppins', system-ui, -apple-system, sans-serif";

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="#fff" aria-hidden="true">
    <path d="M17.05 12.04c-.03-2.4 1.96-3.55 2.05-3.61-1.12-1.64-2.86-1.86-3.48-1.89-1.48-.15-2.89.87-3.64.87-.75 0-1.59-.85-2.62-.83-1.35.02-2.6.78-3.29 1.99-1.4 2.44-.36 6.05 1.01 8.03.67.97 1.46 2.06 2.5 2.02 1-.04 1.38-.65 2.59-.65 1.2 0 1.55.65 2.61.63 1.08-.02 1.76-.99 2.42-1.96.76-1.12 1.07-2.2 1.09-2.26-.02-.01-2.09-.8-2.12-3.18zM14.62 4.6c.55-.67.93-1.6.83-2.53-.8.03-1.77.53-2.34 1.2-.51.59-.96 1.54-.84 2.45.89.07 1.8-.45 2.35-1.12z"/>
  </svg>
);
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path d="M4 3.5v17l9.5-8.5L4 3.5z" fill="#fff"/>
    <path d="M13.5 12l3.2-2.86 3.1 1.74c1 .56 1 1.68 0 2.24l-3.1 1.74L13.5 12z" fill="#fff" opacity=".82"/>
  </svg>
);

const StoreBtn = ({ href, icon, top, bottom }) => (
  <a href={href} className="rnrv-cta" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
    background: '#000', border: `1px solid ${C.border}`, borderRadius: 13, padding: '11px 18px', width: '100%' }}>
    <span style={{ display: 'flex', width: 26, justifyContent: 'center' }}>{icon}</span>
    <span style={{ textAlign: 'left', lineHeight: 1.15 }}>
      <span style={{ display: 'block', color: C.faint, fontSize: 10.5, letterSpacing: '.04em', textTransform: 'uppercase' }}>{top}</span>
      <span style={{ display: 'block', color: C.white, fontSize: 17, fontWeight: 600 }}>{bottom}</span>
    </span>
  </a>
);

class AppDownloadPage extends Component {
  funnelParams() {
    // Router-provided search works during SSR; window covers direct hits.
    const search =
      (this.props.location && this.props.location.search) ||
      (typeof window !== 'undefined' ? window.location.search : '');
    return readFunnelParams(search);
  }

  componentDidMount() {
    try {
      const funnel = this.funnelParams();
      const ua = (navigator.userAgent || '').toLowerCase();
      if (/android/.test(ua)) window.location.replace(playUrlWithReferrer(funnel));
      else if (/iphone|ipad|ipod/.test(ua)) window.location.replace(IOS_URL);
    } catch (e) { /* SSR */ }
  }
  render() {
    const funnel = this.funnelParams();
    const playHref = playUrlWithReferrer(funnel);
    return (
      <>
        <Helmet>
          <title>Get the Ride N Repair App</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Helmet>
        <style>{`.rnrv-cta{transition:filter .15s,transform .1s}.rnrv-cta:hover{filter:brightness(1.12)}.rnrv-cta:active{transform:translateY(1px)}`}</style>
        <div style={{ minHeight: '100vh', background: `radial-gradient(130% 90% at 50% -15%, #1b1f27 0%, ${C.bg} 58%)`,
          color: C.white, fontFamily: FONT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 18px' }}>
          <img src={logoWhite} alt="Ride N Repair" style={{ height: 36, marginBottom: 24 }} />
          <div style={{ width: '100%', maxWidth: 420, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20,
            padding: '34px 24px', textAlign: 'center', boxShadow: '0 26px 64px rgba(0,0,0,.55)' }}>
            <img src={SHIELD} alt="" style={{ width: 84, marginBottom: 16 }} />
            <div style={{ color: C.white, fontSize: 24, fontWeight: 700, margin: '0 0 8px' }}>Roadside repair at your door</div>
            <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
              Book a verified mechanic, track them live, and pay only after you approve — with genuine, serialized parts.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <StoreBtn href={playHref} icon={<PlayIcon />} top="Get it on" bottom="Google Play" />
              <StoreBtn href={IOS_URL} icon={<AppleIcon />} top="Download on the" bottom="App Store" />
            </div>
            <div style={{ color: C.faint, fontSize: 11.5, marginTop: 20 }}>Payments secured by Razorpay · ₹0 until you approve</div>
          </div>
          <Link to="/" style={{ marginTop: 22, color: C.muted, fontSize: 13, textDecoration: 'none' }}>← ridenrepair.com</Link>
        </div>
      </>
    );
  }
}

export default AppDownloadPage;
